/**
 * wsReconnect.ts — politika ponovnega povezovanja WebSocketa (T1630)
 * ==================================================================
 *
 * Zakaj obstaja ta modul
 * ----------------------
 * T1255 je odjemalca naučil, da ob terminalnih close kodah (4001/4003/4004/4005)
 * ne poskuša znova. V resničnem brskalniku ta popravek **ni bil dosegljiv**:
 * zaledje povezavo zavrne **pred** `accept()`
 * (`src/backend/apps/core/ws_suspension.py`), zato zaključni okvir do
 * brskalnika sploh ne pride in ta poroča **1006** — kodo, ki je po zasnovi
 * ponovljiva. Izmerjeno 12.9.2026: na `/billing` pod sysadminovim suspenzom
 * **10 vrstic rokovanja v 80 sekundah** (ročna verifikacija §2.5 D2/D3).
 *
 * Odločitev (Alen, 13.9.2026) je bila **B**: zaledje ohrani popolno zavrnitev
 * pred `accept()` — invariant, ki ga varuje 15 testov v
 * `apps/core/tests/test_ws_suspension.py` — odjemalec pa poskuse zameji tudi
 * tam, kjer razloga ne izve. Razlog uporabnik tako ali tako dobi po HTTP poti:
 * 403 z `ORGANIZATION_SUSPENDED` sproži `OrganizationBlockedScreen`.
 *
 * Zakaj štejemo rokovanja, ne zaprtij
 * -----------------------------------
 * Merilo ni close koda (te v tem primeru ni), ampak **ali se je povezava sploh
 * kdaj odprla**. Zavrnjeno rokovanje — `onopen` ni bil klican — je edini vzorec,
 * ki se ponavlja brez izgleda na uspeh: zavrnitev je odvisna od stanja računa
 * ali pravic in se s ponovnim poskusom ne spremeni. Povezava, ki je bila odprta
 * in je nato padla, je izpad omrežja; ta števec ponastavi in backoff začne od
 * začetka.
 *
 * Odnehanje ni dokončno: klicatelja ga ponastavita ob dogodku `online` in ob
 * vrnitvi zavihka v ospredje (`visibilitychange`). Prebujen prenosnik in
 * povrnjen wifi sta prav tista primera, v katerih bi trda meja uporabnika
 * pustila brez realnega časa do osveževanja strani.
 */

/** Osnovni zamik; podvoji se z vsakim zaporednim poskusom. */
export const WS_RECONNECT_BASE_DELAY_MS = 1000;

/** Zgornja meja zamika — brez nje bi eksponentna rast odnesla minute. */
export const WS_RECONNECT_MAX_DELAY_MS = 30_000;

/**
 * Koliko zaporednih **zavrnjenih rokovanj** prenesemo, preden odnehamo.
 *
 * Šest pomeni 1 + 2 + 4 + 8 + 16 + 30 = 61 s poskušanja. Meja je namenoma nad
 * minuto: ponovni zagon zaledja (deploy, restart `daphne`) mora iti skozi, sicer
 * bi odjemalec odnehal sredi njega in uporabnik bi obtičal do osveževanja
 * strani.
 */
export const WS_MAX_HANDSHAKE_FAILURES = 6;

/** Zamik pred *attempt*-tim ponovnim poskusom (0 = prvi po prekinitvi). */
export function wsReconnectDelayMs(attempt: number): number {
  const n = Number.isFinite(attempt) ? Math.max(0, Math.trunc(attempt)) : 0;
  return Math.min(WS_RECONNECT_BASE_DELAY_MS * 2 ** n, WS_RECONNECT_MAX_DELAY_MS);
}

/** Je zaporednih zavrnjenih rokovanj že toliko, da nadaljnji poskus nima smisla? */
export function wsShouldGiveUp(handshakeFailures: number): boolean {
  return handshakeFailures >= WS_MAX_HANDSHAKE_FAILURES;
}
